/**
 * State reducer for LiftForge
 * Ported from index.html lines 1211-1304
 * Handles all state changes via dispatched actions
 */

import { AppState, AppAction, ScheduleSlot, MultiWeekSchedule } from '@/lib/types';
import { uid, DAYS, toDateStr } from '@/lib/helpers';

/**
 * Create the initial application state
 */
export function makeInitialState(): AppState {
  return {
    scheduleType: 'weekly',
    weeklySchedule: DAYS.map((d) => ({ day: d, workoutId: null })),
    cadenceSchedule: {
      restMode: 'manual',
      autoRestEvery: 3,
      rotation: [],
      currentIndex: 0,
    },
    multiWeekSchedule: {
      cycleLength: 2,
      weeks: [
        DAYS.map((d) => ({ day: d, workoutId: null })),
        DAYS.map((d) => ({ day: d, workoutId: null })),
      ],
      currentWeek: 0,
      startDate: toDateStr(new Date()),
    },
    weeklyOverrides: {},
    configTimestamp: 0,

    workouts: {},
    history: [],

    goal: 'Hypertrophy',
    unitPreference: 'lbs',
    bodyStats: {},
    customExCatMap: {},
    customBodyParts: [],

    weightLog: [],

    trackingSession: null,

    gymProfiles: [],
    activeGymId: '',
    barWeight: 0,

    gapiToken: null,
    spreadsheetId: null,

    aiProvider: '',
    aiApiKey: '',

    showOnboarding: false,
    onboardingStep: 0,

    schemaVersion: 2,
  };
}

/**
 * Set of action types that trigger configTimestamp update
 */
export const CONFIG_ACTIONS = new Set([
  'UPSERT_WORKOUT',
  'DELETE_WORKOUT',
  'SET_SCHEDULE_TYPE',
  'SET_WEEKLY_SCHEDULE',
  'SET_CADENCE_SCHEDULE',
  'SET_MULTIWEEK_SCHEDULE',
  'SET_MULTIWEEK_WEEK',
  'SET_MULTIWEEK_CURRENT',
  'SET_UNIT_PREFERENCE',
  'SET_CUSTOM_EX_CAT',
]);

/**
 * Main reducer function that handles all state changes
 * All 25 action types are handled in a switch statement
 */
export function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    // Schedule actions
    case 'SET_SCHEDULE_TYPE':
      return { ...state, scheduleType: action.scheduleType };

    case 'SET_WEEKLY_SCHEDULE':
      return { ...state, weeklySchedule: action.schedule };

    case 'SET_CADENCE_SCHEDULE':
      return {
        ...state,
        cadenceSchedule: { ...state.cadenceSchedule, ...action.data },
      };

    case 'SET_MULTIWEEK_SCHEDULE':
      return {
        ...state,
        multiWeekSchedule: { ...state.multiWeekSchedule, ...action.data },
      };

    case 'SET_MULTIWEEK_WEEK': {
      const weeks = [...state.multiWeekSchedule.weeks];
      weeks[action.weekIndex] = action.schedule;
      return {
        ...state,
        multiWeekSchedule: { ...state.multiWeekSchedule, weeks },
      };
    }

    case 'SET_MULTIWEEK_CURRENT':
      return {
        ...state,
        multiWeekSchedule: {
          ...state.multiWeekSchedule,
          currentWeek: action.weekIndex,
        },
      };

    case 'SET_WEEKLY_OVERRIDE':
      return {
        ...state,
        weeklyOverrides: {
          ...state.weeklyOverrides,
          [action.date]: action.workoutId,
        },
      };

    case 'CLEAR_WEEKLY_OVERRIDE': {
      const overrides = { ...state.weeklyOverrides };
      delete overrides[action.date];
      return { ...state, weeklyOverrides: overrides };
    }

    // Workout management
    case 'UPSERT_WORKOUT': {
      const id = action.workout.id || uid();
      return {
        ...state,
        workouts: {
          ...state.workouts,
          [id]: { ...action.workout, id } as any,
        },
      };
    }

    case 'DELETE_WORKOUT': {
      const workouts = Object.fromEntries(
        Object.entries(state.workouts).filter(([k]) => k !== action.id)
      );
      return {
        ...state,
        workouts,
        weeklySchedule: state.weeklySchedule.map((d) =>
          d.workoutId === action.id ? { ...d, workoutId: null } : d
        ),
        cadenceSchedule: {
          ...state.cadenceSchedule,
          rotation: state.cadenceSchedule.rotation.filter(
            (r) => r.workoutId !== action.id
          ),
        },
        multiWeekSchedule: {
          ...state.multiWeekSchedule,
          weeks: state.multiWeekSchedule.weeks.map((week) =>
            week.map((d) =>
              d.workoutId === action.id ? { ...d, workoutId: null } : d
            )
          ),
        },
      };
    }

    // Tracking/history actions
    case 'START_TRACKING':
      return { ...state, trackingSession: action.session };

    case 'UPDATE_TRACKING_SET': {
      if (!state.trackingSession) return state;
      const exercises = [...state.trackingSession.exercises];
      const exercise = exercises[action.exerciseIndex];
      if (!exercise) return state;

      // Handle both cases: sets as array (during tracking) or sets as number (template)
      const setsArray = Array.isArray(exercise.sets)
        ? exercise.sets
        : [];
      const sets = [...setsArray];
      sets[action.setIndex] = action.set;
      exercises[action.exerciseIndex] = { ...exercise, sets } as any;

      return {
        ...state,
        trackingSession: {
          ...state.trackingSession,
          exercises,
        },
      };
    }

    case 'ADD_TRACKING_SET': {
      if (!state.trackingSession) return state;
      const exercises = [...state.trackingSession.exercises];
      const exercise = exercises[action.exerciseIndex];
      if (!exercise) return state;

      // Handle both cases: sets as array (during tracking) or sets as number (template)
      const setsArray = Array.isArray(exercise.sets)
        ? exercise.sets
        : [];
      const sets = [...setsArray, action.set];
      exercises[action.exerciseIndex] = { ...exercise, sets } as any;

      return {
        ...state,
        trackingSession: {
          ...state.trackingSession,
          exercises,
        },
      };
    }

    case 'REMOVE_TRACKING_SET': {
      if (!state.trackingSession) return state;
      const exercises = [...state.trackingSession.exercises];
      const exercise = exercises[action.exerciseIndex];
      if (!exercise) return state;

      // Handle both cases: sets as array (during tracking) or sets as number (template)
      if (!Array.isArray(exercise.sets)) return state;
      const sets = exercise.sets.filter((_, i) => i !== action.setIndex);
      exercises[action.exerciseIndex] = { ...exercise, sets } as any;

      return {
        ...state,
        trackingSession: {
          ...state.trackingSession,
          exercises,
        },
      };
    }

    case 'REORDER_TRACKING_EXERCISES': {
      if (!state.trackingSession) return state;
      return {
        ...state,
        trackingSession: {
          ...state.trackingSession,
          exercises: action.newOrder,
        },
      };
    }

    case 'SAVE_WORKOUT': {
      const s = state.trackingSession;
      if (!s) return state;

      // Clean exercises by removing transient 'done' property from sets
      const cleanExercises = s.exercises.map((ex) => ({
        ...ex,
        sets: Array.isArray(ex.sets)
          ? ex.sets.map((st) => {
              const c = { ...st };
              delete (c as any).done;
              return c;
            })
          : [],
      }));

      // Create history entry
      const entry = {
        date: s.editingDate || toDateStr(new Date()),
        workoutId: s.workoutId,
        workoutName: (state.workouts[s.workoutId]?.name || 'Unknown'),
        wasOverride: s.wasOverride || false,
        exercises: cleanExercises,
        duration: 0,
        notes: '',
      };

      // Update or append to history
      let hist = state.history;
      if (s.editingDate) {
        hist = state.history.map((h) =>
          h.date === s.editingDate && h.workoutId === s.workoutId ? entry : h
        );
      } else {
        hist = [...state.history, entry];
      }

      // Advance cadence schedule if not editing and in cadence mode
      let cad = state.cadenceSchedule;
      if (
        !s.editingDate &&
        state.scheduleType === 'cadence' &&
        cad.rotation.length > 0
      ) {
        cad = {
          ...cad,
          currentIndex: (cad.currentIndex + 1) % cad.rotation.length,
        };
      }

      return {
        ...state,
        history: hist,
        cadenceSchedule: cad,
        trackingSession: null,
      };
    }

    // Cloud sync
    case 'LOAD_FROM_SHEETS': {
      // Only load allowed keys
      const allowedKeys: (keyof AppState)[] = [
        'scheduleType',
        'weeklySchedule',
        'cadenceSchedule',
        'multiWeekSchedule',
        'workouts',
        'history',
        'goal',
        'unitPreference',
        'customExCatMap',
        'configTimestamp',
      ];

      const clean: Partial<AppState> = {};
      allowedKeys.forEach((k) => {
        if (action.data[k] !== undefined) {
          (clean as any)[k] = action.data[k];
        }
      });

      return { ...state, ...clean };
    }

    // Authentication
    case 'SET_GAPI_TOKEN':
      return { ...state, gapiToken: action.token };

    case 'SET_SPREADSHEET_ID':
      return { ...state, spreadsheetId: action.id };

    // AI configuration
    case 'SET_AI_PROVIDER':
      return { ...state, aiProvider: action.provider };

    case 'SET_AI_API_KEY':
      return { ...state, aiApiKey: action.key };

    // User preferences
    case 'SET_GOAL':
      return { ...state, goal: action.goal };

    case 'SET_UNIT_PREFERENCE':
      return { ...state, unitPreference: action.unit };

    case 'SET_BODY_STATS':
      return { ...state, bodyStats: action.stats };

    case 'SET_CUSTOM_EX_CAT':
      return {
        ...state,
        customExCatMap: {
          ...state.customExCatMap,
          [action.name]: action.category,
        },
      };

    // Gym profiles
    case 'SET_GYM_PROFILES':
      return { ...state, gymProfiles: action.profiles };

    case 'SET_ACTIVE_GYM':
      return { ...state, activeGymId: action.gymId };

    case 'SET_BAR_WEIGHT':
      return { ...state, barWeight: action.weight };

    default:
      return state;
  }
}

/**
 * Wrapper around reducer that automatically updates configTimestamp
 * for configuration-changing actions
 */
export function reducerWithTimestamp(
  state: AppState,
  action: AppAction
): AppState {
  const next = reducer(state, action);

  // Update timestamp only if state changed and action is a config action
  if (next !== state && CONFIG_ACTIONS.has(action.type)) {
    return { ...next, configTimestamp: Date.now() };
  }

  return next;
}
